import type { ActiveHintStage } from "@/game/feedback";

import { defineCurriculumVoiceCue, type CurriculumVoiceCue } from "./cue";

const TARGET_SEARCH_HINT_TEXT: Readonly<Record<ActiveHintStage, string>> = {
  "gentle-hint": "点点悄悄点亮了一个目标，看看“提示”标记。",
  "reduced-choices": "藏起来的选项变少啦，把剩下的再比一比。",
  "replay-demonstration": "跟着亮起来的目标看一遍，再找同样的特征。"
};

const TARGET_SEARCH_HINT_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = Object.fromEntries(
  Object.entries(TARGET_SEARCH_HINT_TEXT).map(([stage, text]) => [
    stage,
    defineCurriculumVoiceCue(
      "/audio/focus-social/feedback/target-search-hint-" + stage + ".ogg",
      text
    )
  ])
) as Record<ActiveHintStage, CurriculumVoiceCue>;

const TARGET_SEARCH_RETRY_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = Object.fromEntries(
  Object.entries(TARGET_SEARCH_HINT_TEXT).map(([stage, text]) => [
    stage,
    defineCurriculumVoiceCue(
      "/audio/focus-social/feedback/target-search-retry-" + stage + ".ogg",
      "差一点，它藏在别处。" + text
    )
  ])
) as Record<ActiveHintStage, CurriculumVoiceCue>;

const FOLLOW_DIRECTIONS_HINT_TEXT: Readonly<
  Record<ActiveHintStage, string>
> = {
  "gentle-hint": "看看现在轮到哪一步，先把它找出来。",
  "reduced-choices": "选项变少了。照着刚才的顺序，接着来。",
  "replay-demonstration":
    "点点把整串顺序再摆一遍。看到现在这一步，再继续。"
};

const FOLLOW_DIRECTIONS_HINT_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = Object.fromEntries(
  Object.entries(FOLLOW_DIRECTIONS_HINT_TEXT).map(([stage, text]) => [
    stage,
    defineCurriculumVoiceCue(
      "/audio/focus-social/feedback/follow-directions-hint-" + stage + ".ogg",
      text
    )
  ])
) as Record<ActiveHintStage, CurriculumVoiceCue>;

const FOLLOW_DIRECTIONS_RETRY_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = Object.fromEntries(
  Object.entries(FOLLOW_DIRECTIONS_HINT_TEXT).map(([stage, text]) => [
    stage,
    defineCurriculumVoiceCue(
      "/audio/focus-social/feedback/follow-directions-retry-" + stage + ".ogg",
      "这一步还没轮到。" + text
    )
  ])
) as Record<ActiveHintStage, CurriculumVoiceCue>;

export const FOLLOW_DIRECTIONS_PROGRESS_CUE = defineCurriculumVoiceCue(
  "/audio/focus-social/feedback/follow-directions-progress.ogg",
  "对啦，这一步接上了。下一步是谁？"
);

export const FOLLOW_DIRECTIONS_UNDONE_CUE = defineCurriculumVoiceCue(
  "/audio/focus-social/feedback/follow-directions-undone.ogg",
  "回到前一步啦，重新选就好。"
);

export const FOLLOW_DIRECTIONS_REPLAYED_CUE = defineCurriculumVoiceCue(
  "/audio/focus-social/feedback/follow-directions-replayed.ogg",
  "点点把完整顺序又念了一遍。"
);

const FRIENDSHIP_HINT_CUES: Readonly<
  Record<ActiveHintStage, CurriculumVoiceCue>
> = {
  "gentle-hint": defineCurriculumVoiceCue(
    "/audio/focus-social/feedback/friendship-gentle-hint.ogg",
    "听听每句话的感觉：哪一句既说清需要，也给朋友留了空间？"
  ),
  "reduced-choices": defineCurriculumVoiceCue(
    "/audio/focus-social/feedback/friendship-reduced-choices.ogg",
    "只剩两个想法啦。想想选了以后，两个人会有什么感受。"
  ),
  "replay-demonstration": defineCurriculumVoiceCue(
    "/audio/focus-social/feedback/friendship-replay-demonstration.ogg",
    "故事再讲一遍。听听每个人现在想要什么、需要什么。"
  )
};

export function getTargetSearchFeedbackCue(
  stage: ActiveHintStage,
  kind: "hint" | "retry"
): CurriculumVoiceCue {
  return kind === "retry"
    ? TARGET_SEARCH_RETRY_CUES[stage]
    : TARGET_SEARCH_HINT_CUES[stage];
}

export function getFollowDirectionsFeedbackCue(
  stage: ActiveHintStage,
  kind: "hint" | "retry"
): CurriculumVoiceCue {
  return kind === "retry"
    ? FOLLOW_DIRECTIONS_RETRY_CUES[stage]
    : FOLLOW_DIRECTIONS_HINT_CUES[stage];
}

export function getFriendshipHintCue(
  stage: ActiveHintStage
): CurriculumVoiceCue {
  return FRIENDSHIP_HINT_CUES[stage];
}

export const FOCUS_SOCIAL_STATIC_FEEDBACK_CUES = [
  ...Object.values(TARGET_SEARCH_HINT_CUES),
  ...Object.values(TARGET_SEARCH_RETRY_CUES),
  ...Object.values(FOLLOW_DIRECTIONS_HINT_CUES),
  ...Object.values(FOLLOW_DIRECTIONS_RETRY_CUES),
  FOLLOW_DIRECTIONS_PROGRESS_CUE,
  FOLLOW_DIRECTIONS_UNDONE_CUE,
  FOLLOW_DIRECTIONS_REPLAYED_CUE,
  ...Object.values(FRIENDSHIP_HINT_CUES)
] as const;
